/**
 * Template System V2
 * 
 * A simplified, focused approach to the template system that:
 * 1. Only processes the fields specified in the template
 * 2. Uses field-specific system prompts
 * 3. Provides clear progress tracking and error handling
 * 
 * This file serves as the main entry point for the V2 template system.
 */
import { executeTemplates } from './templateExecutionServiceV2';

/**
 * Execute templates on products
 * @param {Array|string} templateIds - The IDs of templates to apply, or a single template ID
 * @param {Array} products - The products to process
 * @param {Function} onProgress - Progress callback
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} The execution results
 */
export const executeTemplatesV2 = async (templateIds, products, onProgress = null, options = {}) => {
  try {
    // Normalize templateIds to array
    const templateIdArray = Array.isArray(templateIds) ? templateIds : [templateIds];
    
    console.log('Executing templates V2:', {
      templateCount: templateIdArray.length,
      productCount: products.length
    });
    
    // Execute templates with the correct parameter order
    const results = await executeTemplates(
      templateIdArray,
      products, 
      [], // Templates will be loaded by ID
      {
        onProgress,
        userId: options.userId,
        shopId: options.shopId,
        // If getFieldValue is provided, use it, otherwise use a simple default implementation
        getFieldValue: options.getFieldValue || ((product, field) => {
          return product[field];
        }),
        onComplete: options.onComplete,
        ...options
      }
    );
    
    return results;
  } catch (error) {
    console.error('Error executing templates V2:', error);
    throw error;
  }
};

/**
 * Example usage:
 * 
 * import { executeTemplatesV2 } from './services/templateSystemV2';
 * 
 * const results = await executeTemplatesV2(
 *   templateIds,
 *   products,
 *   (progress) => {
 *     console.log(`Progress: ${Math.round(progress * 100)}%`);
 *   },
 *   {
 *     shopId,
 *     onComplete: (results) => {
 *       console.log('Execution complete:', results);
 *     }
 *   }
 * );
 */
